'use client';
import { useMemo, useState } from 'react';

import { capitalize } from 'lodash';
import { useMediaQuery } from 'usehooks-ts';

import { FormToggleButtonRadioGroup } from '@ui/uikit/components/common/FormToggleButtonRadioGroup';
import { Table, TableColumn } from '@ui/uikit/components/common/Table';
import { Typography } from '@ui/uikit/components/common/Typography';
import { BnbChain, USDT } from '@ui/uikit/components/icons/poly';

import {
  options,
  TabName,
} from '../../../../../MagicRafflesPage/ui/MagicRaffleEntries/MagicRaffleEntries';
import { usePredictionEntries } from '../../../../api/usePredictionRounds/usePredictionEntries';
import { PredictionEntry } from '../../../../model/PredictionEntry';
import { ContentWrapper } from './cells/ContentWrapper';
import { MyRewardCell } from './cells/MyRewardCell';
import { PredictionCell } from './cells/PredictionCell';

const columns: TableColumn<PredictionEntry>[] = [
  {
    name: 'roundId',
    label: 'Prediction',
    render: ({ row }) => {
      return (
        <PredictionCell
          icon={
            <span className="flex items-center">
              <BnbChain className="icon-xs" />
              <USDT className="icon-xs -ml-2" />
            </span>
          }
          roundNumber={row.roundId}
          timestamp={row.closeTimestamp}
        />
      );
    },
  },
  {
    name: 'userChoice',
    label: 'My Choice',
    render: ({ row }) => {
      return (
        <ContentWrapper>
          {capitalize(row.userChoice)}
          <Typography variant="caption-s" color="tertiary">
            {row.userBet?.formatted}
          </Typography>
        </ContentWrapper>
      );
    },
  },
  {
    name: 'roundResult',
    label: 'Result',
    render: ({ row }) => {
      const result = row.isClaimed ? (
        capitalize(row.roundResult)
      ) : (
        <Typography variant="caption-s" color="tertiary">
          —
        </Typography>
      );

      return <ContentWrapper>{result}</ContentWrapper>;
    },
  },
  {
    name: 'wonAmount',
    label: 'My Reward',
    render: ({ row }) => (
      <MyRewardCell
        value={row.wonAmount.value}
        formattedValue={row.wonAmount.formatted}
        isClaimed={row.isClaimed}
      />
    ),
  },
];

const ROWS_PER_PAGE = 5;
const DEFAULT_EMPTY_STATE_COMPONENT = (
  <Typography variant="caption-s" color="tertiary" textAlign="center">
    No predictions yet.
    <br />
    Make your first prediction now!
  </Typography>
);

export const PredictionEntries = () => {
  const [activeTab, setActiveTab] = useState<TabName>('all');
  const [currentPage, setCurrentPage] = useState(0);
  const { data: entries = [], isLoading } = usePredictionEntries();
  const isLargeScreen = useMediaQuery('(min-width: 1440px)');

  const filteredRounds = useMemo(() => {
    return activeTab === 'all' ? entries : entries.filter((entry) => entry.wonAmount.value > 0);
  }, [activeTab, entries]);

  const displayedRounds = useMemo(() => {
    return filteredRounds.slice(0, (currentPage + 1) * ROWS_PER_PAGE);
  }, [filteredRounds, currentPage]);

  const isShowMoreVisible = filteredRounds.length > displayedRounds.length;

  const restPagesCount = filteredRounds.length - displayedRounds.length;

  const gridTemplateColumns = useMemo(() => {
    return isLargeScreen ? '464px 174px 120px 154px' : '420px 152px 100px 152px';
  }, [isLargeScreen]);

  const onClickTabHandler = (tab: TabName) => {
    setActiveTab(tab);
    setCurrentPage(0);
  };

  const onClickShowMore = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-col gap-4 md-msq:flex-row  md-msq:justify-between md-msq:gap-0 ">
        <Typography variant="h2">Prediction Entries</Typography>
        <FormToggleButtonRadioGroup
          wrapperClassName="max-sm-msq:w-full"
          className="w-full md:w-auto bg-base-bg-secondary text-base-text-tertiary rounded-lg"
          options={options}
          value={activeTab}
          onValueChange={onClickTabHandler}
        />
      </div>
      <Table
        columns={columns}
        data={displayedRounds}
        isLoading={isLoading}
        emptyStateComponent={DEFAULT_EMPTY_STATE_COMPONENT}
        gridTemplateColumns={gridTemplateColumns}
      />
      {isShowMoreVisible && (
        <button
          className="msq-btn msq-btn-secondary msq-btn-md mt-6 max-sm-msq:w-full"
          onClick={onClickShowMore}
          disabled={isLoading}
        >
          Show Next {Math.min(ROWS_PER_PAGE, restPagesCount)}
        </button>
      )}
    </div>
  );
};
